'use client'

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { unparse, parse } from 'papaparse'
import prettyBytes from 'pretty-bytes'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { DatabaseProvider } from './DatabaseProvider'
import { Button } from '@/components/ui/button'
import { useTranslations } from 'next-intl'
import { Download, FileText, RefreshCcw } from 'lucide-react'
import { useDatabaseContext } from './context'
import {
  AUTO_GENERATED_FIELDS,
  COLUMN_TYPE_MAP,
  parseData,
  validateColumnType,
} from './shared'
import { downloadAsCSV, fetcher } from '@/lib/utils'
import { isString } from 'lodash'
import { FileUpload } from '@/components/ui/file-upload'
import { toast } from 'sonner'
import type { TableFieldDefinition, TableFieldType } from '@/lib/types'
import type { ImportResult } from './types'
import { BatchImportConfirm } from './components/BatchImportResult'

export interface BatchActionsProps {
  tableName: string
  csvTemplateData?: Record<string, any>[]
  children: ReactNode
  onOk?: () => void
}

export function BatchActions(props: BatchActionsProps) {
  const { tableName, onOk, children, csvTemplateData } = props
  const { selectedTableColumn, project } = useDatabaseContext()
  const t = useTranslations('database')

  const [open, setOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [csvData, setCsvData] = useState<any[] | undefined>()
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | undefined>()
  // fake progress
  const [progress, setProgress] = useState(0)

  const isValidCsvData = useMemo(
    () => Array.isArray(csvData) && csvData.length > 0,
    [csvData],
  )

  const reader = new FileReader()

  const resetFile = useCallback(() => {
    setError(undefined)
    setUploadFile(undefined)
    setCsvData(undefined)
    setImporting(false)
    setProgress(0)
    setImportResult(undefined)
  }, [])

  const toggle = useCallback(
    (open: boolean) => {
      resetFile()
      setOpen(open)
    },
    [resetFile],
  )

  // db fields
  const fields = useMemo(
    () =>
      (selectedTableColumn?.field_list || []).filter(
        (d) => !AUTO_GENERATED_FIELDS.includes(d.db_name),
      ),
    [selectedTableColumn],
  )

  const handleDownlaodCSV = useCallback(() => {
    downloadAsCSV(
      unparse({
        fields: fields.map((d) => d.ui_name),
        data: (csvTemplateData || []).map((d) => Object.values(d)),
      }),
      tableName,
    )
  }, [csvTemplateData, fields, tableName])

  useEffect(() => {
    if (importing && progress < 95) {
      const timer = setInterval(() => {
        setProgress((p) => Math.min(p + Math.random() * 5, 95))
      }, 500)
      return () => clearInterval(timer)
    }
  }, [importing, progress])

  /**
   *
   * 1. 所有字段必须是 field_list 中声明过的字段。
   * 2. 超出 field_list 的字段将被过滤。
   * 3. 对于 required: true 的字段，字段值不能为空（null, undefined, '' 均不允许）。
   * 4. 如果 required: true 的字段缺失，则返回错误。
   * 5. 如果所有字段都合法，返回清洗过的数据。
   */
  const validateCSVData = (
    rawData: any[],
    fieldList: TableFieldDefinition[],
  ): { valid: boolean; errors?: string[]; data?: any[] } => {
    const fieldMap = new Map(
      fieldList.map((f) => [
        f.db_name,
        { type: f.type, required: f.required, uiName: f.ui_name },
      ]),
    )
    const fieldNameMap = new Map(
      fieldList.map((f) => [
        f.ui_name,
        { type: f.type, required: f.required, dbName: f.db_name },
      ]),
    )
    const requiredFields = fieldList
      .filter((f) => f.required && !AUTO_GENERATED_FIELDS.includes(f.db_name))
      .map((f) => f.db_name)
    const allowedFields = new Set(fieldList.map((f) => f.db_name))

    const errors: string[] = []
    if (!Array.isArray(rawData) || rawData.length === 0) {
      errors.push(t('batchActions.data_is_empty'))
    }
    const cleanedData = rawData.map((row, rowIndex) => {
      const cleanedRow: Record<string, any> = {}
      for (const d in row) {
        const key = fieldNameMap.get(d)?.dbName
        if (!key || !allowedFields.has(key)) {
          continue // 过滤未知字段
        }

        const value = row[d]
        const { required, type } = fieldMap.get(key)!

        if (
          required &&
          (value === undefined || value === null || value === '')
        ) {
          errors.push(
            t('batchActions.validation.required_field', {
              field: key,
              row: rowIndex + 1,
            }),
          )
        }

        const supabaseTable = (project?.supabase_tables || []).find(
          (table) => table.name === tableName,
        )
        if (!supabaseTable)
          return {
            errors: [new Error('Database not exsit')],
            data: [],
          }
        const { columns } = supabaseTable
        const col = columns.find((d) => d.name === key)
        if (!col) {
          errors.push(`col: ${key} not exist`)
        } else {
          const validColumnType = validateColumnType(value, col)
          if (validColumnType === false) {
            errors.push(
              t('batchActions.validation.type_field', {
                field: key,
                type: col.type,
                row: rowIndex + 1,
              }),
            )
          }
        }

        cleanedRow[key] = parseData(value, type as TableFieldType)
      }

      // 填充缺失字段为 undefined
      for (const reqField of requiredFields) {
        if (!(reqField in cleanedRow)) {
          errors.push(
            t('batchActions.validation.missing_field', {
              field: reqField,
              row: rowIndex + 1,
            }),
          )
          cleanedRow[reqField] = undefined
        }
      }

      return cleanedRow
    })

    if (errors.length > 0) {
      return { valid: false, errors }
    }

    return { valid: true, data: cleanedData }
  }

  reader.onload = (event) => {
    const csvString = event.target?.result
    if (csvString && isString(csvString)) {
      // 可以在这里对csvString进行清理或检查
      const cleanedCsvString = csvString.replace(/\r?\n/g, '\r\n') // 统一换行符
      parse(cleanedCsvString, {
        header: true, // 解析为对象，首行作为字段名
        skipEmptyLines: true,
        complete: (results) => {
          if (results?.errors && results?.errors.length > 0) {
            setError(results.errors[0].message)
            return
          }
          const { errors, data } = validateCSVData(results.data, fields)
          if (errors && errors?.length > 0) {
            setError(errors[0])
          } else {
            if (Array.isArray(data) && data.length > 0) {
              setCsvData(data)
            }
          }
        },
        error: (err: any) => {
          console.error('Parse Error:', err)
          setError(err.message)
        },
      })
    }
  }

  const handleBeforeUpload = async (files: File[]) => {
    resetFile()
    if (files && files.length === 1) {
      setUploadFile(files[0])
      reader.readAsText(files[0])
    }

    return true
  }

  const handleImport = async () => {
    if (!project) return
    setImporting(true)

    const response = await fetcher(
      `/api/me/project/${project._id}/table/${tableName}/batchImport`,
      {
        method: 'POST',
        body: JSON.stringify(csvData),
      },
    )
      .then((res: any) => {
        if (res.success) {
          if (res.data.inserted > 0) {
            toast(res.message)
            setImportResult(res.data as ImportResult)
          } else {
            setImportResult(undefined)
            toast(t('batchActions.data_valid'))
          }
        }
      })
      .catch((err) => {
        setImportResult(undefined)
        toast(err.message)
      })
      .finally(() => {
        setImporting(false)
      })
  }

  const previewRender = (file: string | File) => {
    if (typeof file === 'string') return null
    return (
      <div className="w-full flex items-center justify-between gap-4 overflow-hidden">
        <div className="flex items-center flex-1 min-w-0 gap-2">
          <FileText className="text-brand_2 shrink-0" size={32} />
          {importing === true ? (
            <div className="min-w-0 flex-1">
              <Progress value={progress} />
              <p className="text-muted-foreground mt-2">
                {progress < 100
                  ? t('batchActions.importing_desc')
                  : t('batchActions.import_complete')}
              </p>
            </div>
          ) : (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{file.name}</div>
              <div className="text-md text-muted-foreground">
                {prettyBytes(file.size)}
              </div>
            </div>
          )}
        </div>
        {importing === false && (
          <Button
            variant="default"
            className="shrink-0 w-[120px]"
            icon={<RefreshCcw />}
            onClick={resetFile}
          >
            {t('batchActions.reupload')}
          </Button>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={toggle}>
      <DialogTitle className="hidden">{t('batchActions.title')}</DialogTitle>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="container mx-auto h-full flex flex-col bg-[#0C1522]"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="hidden">{t('database')}</DialogTitle>
        </DialogHeader>
        <div className="flex justify-between items-center h-auto mb-2">
          <div className="flex items-center gap-2">
            <div className="text-xl">{t('batchActions.title')}</div>
          </div>
        </div>
        {importResult ? (
          <BatchImportConfirm {...importResult} setOpen={setOpen} onOk={onOk} />
        ) : (
          <>
            <div className="flex-1 flex flex-col overflow-auto">
              <div className="flex-1">
                <div className="flex-center bg-accent text-accent-foreground p-4 mb-6">
                  <div className="flex-1">
                    <h3 className="text-xl">
                      {t('batchActions.download_template.title')}
                    </h3>
                    <p className="mt-2 text-accent-foreground/60">
                      {t('batchActions.download_template.description')}
                    </p>
                  </div>
                  <Button
                    variant="transparent"
                    icon={<Download />}
                    onClick={handleDownlaodCSV}
                  >
                    {t('batchActions.download_template.button')}
                  </Button>
                </div>

                <div className="bg-accent text-accent-foreground p-4 mb-2">
                  <h3 className="text-xl mb-4">
                    {t('batchActions.upload_file.title')}
                  </h3>
                  <FileUpload
                    value={uploadFile}
                    accept={['.csv']}
                    assetDir="user-assets"
                    autoUpload={false}
                    maxFiles={1}
                    beforeUpload={handleBeforeUpload}
                    previewRender={previewRender}
                  />
                  <div className="mt-4">
                    <div>{t('batchActions.tips')}:</div>
                    <p className="text-gray-400 text-sm">
                      {t('batchActions.table_join_message')}
                    </p>
                  </div>
                </div>

                {error && <div className="text-red-500 text-sm">{error}</div>}
              </div>
              <div className="flex justify-end gap-4">
                <Button variant="accent" onClick={() => setOpen(false)}>
                  {t('batchActions.cancel')}
                </Button>
                <Button
                  variant="default"
                  onClick={handleImport}
                  disabled={error !== undefined || importing || !isValidCsvData}
                  loading={importing}
                >
                  {importing === true
                    ? t('batchActions.importing')
                    : t('batchActions.start_import')}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default function DatabaseBatchActions(props: BatchActionsProps) {
  const { tableName } = props
  return (
    <DatabaseProvider tableName={tableName}>
      <BatchActions {...props} />
    </DatabaseProvider>
  )
}
